import * as Yup from "yup";
import React, { useContext, useState, useEffect, useCallback } from "react";
import { Link, useHistory } from "react-router-dom";
import {
  Visibility,
  VisibilityOff,
  WhatsApp,
  Email as EmailIcon,
} from "@mui/icons-material";
import { useFormik } from "formik";
import imgbgregister from "../../assets/img/registration-form__bg.png";
import AuthContext from "../../Context/auth/AuthContext";
import Swal from "sweetalert2";

import {
  Box,
  IconButton,
  InputAdornment,
  TextField,
  FormControl,
  InputLabel,
  OutlinedInput,
  FormHelperText,
  Button,
  Typography,
  Paper,
  Stepper,
  Step,
  StepLabel,
  Alert,
  CircularProgress,
  Chip,
  ToggleButtonGroup,
  ToggleButton,
} from "@mui/material";

const RESEND_COOLDOWN_SECONDS = 180;

const steps = ["Solicitar código", "Verificar código", "Nueva contraseña"];

// ─── Step 1: Phone or Email ──────────────────────────────────────────────────
const StepRequestCode = ({ formik, loading, method, onMethodChange }) => (
  <>
    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
      Elige cómo deseas recibir tu código de verificación.
    </Typography>

    <ToggleButtonGroup
      value={method}
      exclusive
      onChange={(_, val) => val && onMethodChange(val)}
      fullWidth
      size="small"
      sx={{
        mb: 2,
        "& .Mui-selected": {
          backgroundColor: "#801F56 !important",
          color: "#fff !important",
        },
      }}
    >
      <ToggleButton value="whatsapp" sx={{ fontWeight: 600, textTransform: "none" }}>
        <WhatsApp sx={{ mr: 1, fontSize: 20 }} /> WhatsApp
      </ToggleButton>
      <ToggleButton value="email" sx={{ fontWeight: 600, textTransform: "none" }}>
        <EmailIcon sx={{ mr: 1, fontSize: 20 }} /> Correo electrónico
      </ToggleButton>
    </ToggleButtonGroup>

    {method === "whatsapp" ? (
      <TextField
        fullWidth
        id="number_phone"
        label="Número Telefónico (10 dígitos)"
        name="number_phone"
        type="tel"
        inputProps={{ maxLength: 10 }}
        value={formik.values.number_phone}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        error={formik.touched.number_phone && Boolean(formik.errors.number_phone)}
        helperText={formik.touched.number_phone && formik.errors.number_phone}
        margin="normal"
      />
    ) : (
      <TextField
        fullWidth
        id="email"
        label="Correo electrónico"
        name="email"
        type="email"
        value={formik.values.email}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        error={formik.touched.email && Boolean(formik.errors.email)}
        helperText={formik.touched.email && formik.errors.email}
        margin="normal"
      />
    )}

    <Button
      fullWidth
      type="submit"
      variant="contained"
      disabled={loading}
      startIcon={
        loading ? (
          <CircularProgress size={20} color="inherit" />
        ) : method === "whatsapp" ? (
          <WhatsApp />
        ) : (
          <EmailIcon />
        )
      }
      sx={{
        mt: 2,
        py: 1.4,
        backgroundColor: "#801F56",
        "&:hover": { backgroundColor: "#6a1847" },
        fontWeight: 600,
        fontSize: "0.95rem",
      }}
    >
      {loading
        ? "Enviando..."
        : method === "whatsapp"
          ? "Enviar código por WhatsApp"
          : "Enviar código por correo"}
    </Button>
  </>
);

// ─── Step 2: OTP Code ────────────────────────────────────────────────────────
const StepCode = ({ formik, loading, countdown, onResend, method, phone, email }) => {
  const displayTarget =
    method === "whatsapp"
      ? `****${(phone || "").slice(-4)} por WhatsApp`
      : `${email} por correo electrónico`;

  return (
    <>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Enviamos un código de 6 dígitos a{" "}
        <strong>{displayTarget}</strong>.
      </Typography>

      <TextField
        fullWidth
        id="code"
        label="Código de verificación"
        name="code"
        type="text"
        inputProps={{
          maxLength: 6,
          style: {
            letterSpacing: "0.5em",
            textAlign: "center",
            fontSize: "1.4rem",
          },
        }}
        value={formik.values.code}
        onChange={(e) => {
          const val = e.target.value.replace(/\D/g, "");
          formik.setFieldValue("code", val);
        }}
        onBlur={formik.handleBlur}
        error={formik.touched.code && Boolean(formik.errors.code)}
        helperText={formik.touched.code && formik.errors.code}
        margin="normal"
        placeholder="000000"
      />

      <Button
        fullWidth
        type="submit"
        variant="contained"
        disabled={loading}
        sx={{
          mt: 2,
          py: 1.4,
          backgroundColor: "#801F56",
          "&:hover": { backgroundColor: "#6a1847" },
          fontWeight: 600,
          fontSize: "0.95rem",
        }}
      >
        {loading ? (
          <CircularProgress size={22} color="inherit" />
        ) : (
          "Verificar código"
        )}
      </Button>

      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          mt: 2,
          gap: 1,
        }}
      >
        {countdown > 0 ? (
          <Chip
            label={`Reenviar en ${Math.floor(countdown / 60)}:${String(
              countdown % 60
            ).padStart(2, "0")}`}
            variant="outlined"
            size="small"
            sx={{ color: "#801F56", borderColor: "#801F56" }}
          />
        ) : (
          <Button
            size="small"
            onClick={onResend}
            disabled={loading}
            startIcon={
              method === "whatsapp" ? (
                <WhatsApp fontSize="small" />
              ) : (
                <EmailIcon fontSize="small" />
              )
            }
            sx={{ color: "#801F56", textTransform: "none", fontWeight: 600 }}
          >
            Reenviar código
          </Button>
        )}
      </Box>
    </>
  );
};

// ─── Step 3: New Password ────────────────────────────────────────────────────
const StepNewPassword = ({ formik, loading }) => {
  const [showPwd, setShowPwd] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  return (
    <>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Ingresa tu nueva contraseña. Debe tener al menos 8 caracteres.
      </Typography>

      <FormControl
        fullWidth
        variant="outlined"
        margin="normal"
        error={formik.touched.password && Boolean(formik.errors.password)}
      >
        <InputLabel htmlFor="password">Nueva Contraseña</InputLabel>
        <OutlinedInput
          id="password"
          name="password"
          type={showPwd ? "text" : "password"}
          value={formik.values.password}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          endAdornment={
            <InputAdornment position="end">
              <IconButton onClick={() => setShowPwd(!showPwd)} edge="end">
                {showPwd ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          }
          label="Nueva Contraseña"
        />
        {formik.touched.password && formik.errors.password && (
          <FormHelperText>{formik.errors.password}</FormHelperText>
        )}
      </FormControl>

      <FormControl
        fullWidth
        variant="outlined"
        margin="normal"
        error={
          formik.touched.password_confirmation &&
          Boolean(formik.errors.password_confirmation)
        }
      >
        <InputLabel htmlFor="password_confirmation">
          Confirmar Contraseña
        </InputLabel>
        <OutlinedInput
          id="password_confirmation"
          name="password_confirmation"
          type={showConfirm ? "text" : "password"}
          value={formik.values.password_confirmation}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          endAdornment={
            <InputAdornment position="end">
              <IconButton
                onClick={() => setShowConfirm(!showConfirm)}
                edge="end"
              >
                {showConfirm ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          }
          label="Confirmar Contraseña"
        />
        {formik.touched.password_confirmation &&
          formik.errors.password_confirmation && (
            <FormHelperText>
              {formik.errors.password_confirmation}
            </FormHelperText>
          )}
      </FormControl>

      <Button
        fullWidth
        type="submit"
        variant="contained"
        disabled={loading}
        sx={{
          mt: 2,
          py: 1.4,
          backgroundColor: "#801F56",
          "&:hover": { backgroundColor: "#6a1847" },
          fontWeight: 600,
          fontSize: "0.95rem",
        }}
      >
        {loading ? (
          <CircularProgress size={22} color="inherit" />
        ) : (
          "Cambiar contraseña"
        )}
      </Button>
    </>
  );
};

// ─── Main Component ──────────────────────────────────────────────────────────
const ResetPassword = () => {
  const {
    requestResetCode,
    requestResetByEmail,
    verifyResetCode,
    resetPasswordOtp,
  } = useContext(AuthContext);
  const history = useHistory();

  const [activeStep, setActiveStep] = useState(0);
  const [method, setMethod] = useState("whatsapp"); // "whatsapp" | "email"
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [warningMsg, setWarningMsg] = useState("");
  const [countdown, setCountdown] = useState(0);

  // Countdown timer
  useEffect(() => {
    if (countdown <= 0) return;
    const timer = setInterval(() => {
      setCountdown((prev) => (prev <= 1 ? 0 : prev - 1));
    }, 1000);
    return () => clearInterval(timer);
  }, [countdown]);

  const startCooldown = useCallback(() => {
    setCountdown(RESEND_COOLDOWN_SECONDS);
  }, []);

  // Build the identifier object for verify/reset calls
  const getIdentifier = useCallback(
    (values) =>
      method === "whatsapp"
        ? { number_phone: values.number_phone }
        : { email: values.email },
    [method]
  );

  // ── Validation schemas per step ──
  const getValidationSchema = useCallback(() => {
    if (activeStep === 0) {
      return method === "whatsapp"
        ? Yup.object({
          number_phone: Yup.string()
            .matches(/^\d{10}$/, "El número debe tener exactamente 10 dígitos")
            .required("Número telefónico requerido"),
        })
        : Yup.object({
          email: Yup.string()
            .email("Correo electrónico no válido")
            .required("Correo electrónico requerido"),
        });
    }
    if (activeStep === 1) {
      return Yup.object({
        code: Yup.string()
          .matches(/^\d{6}$/, "El código debe tener 6 dígitos")
          .required("Código requerido"),
      });
    }
    return Yup.object({
      password: Yup.string()
        .min(8, "Mínimo 8 caracteres")
        .required("Contraseña requerida"),
      password_confirmation: Yup.string()
        .oneOf([Yup.ref("password"), null], "Las contraseñas deben coincidir")
        .required("Confirma tu contraseña"),
    });
  }, [activeStep, method]);

  const formik = useFormik({
    initialValues: {
      number_phone: "",
      email: "",
      code: "",
      password: "",
      password_confirmation: "",
    },
    validationSchema: getValidationSchema(),
    validateOnChange: true,
    enableReinitialize: false,
    onSubmit: async (values) => {
      setLoading(true);
      setErrorMsg("");
      setWarningMsg("");

      try {
        if (activeStep === 0) {
          let result;
          if (method === "whatsapp") {
            result = await requestResetCode(values.number_phone);
          } else {
            result = await requestResetByEmail(values.email);
          }
          if (result.success) {
            startCooldown();
            setActiveStep(1);
          } else if (result.rateLimit) {
            setWarningMsg(result.message);
          } else {
            setErrorMsg(result.message);
          }
        } else if (activeStep === 1) {
          const identifier = getIdentifier(values);
          const result = await verifyResetCode(identifier, values.code);
          if (result.success) {
            setActiveStep(2);
          } else if (result.rateLimit) {
            setWarningMsg(result.message);
          } else {
            setErrorMsg(result.message);
          }
        } else if (activeStep === 2) {
          const identifier = getIdentifier(values);
          const result = await resetPasswordOtp({
            ...identifier,
            code: values.code,
            password: values.password,
            password_confirmation: values.password_confirmation,
          });
          if (result.success) {
            setTimeout(() => {
              history.push("/iniciar-sesion");
            }, 2600);
          } else if (result.rateLimit) {
            setWarningMsg(result.message);
          } else {
            setErrorMsg(result.message);
          }
        }
      } catch (err) {
        setErrorMsg("Ocurrió un error inesperado. Intenta de nuevo.");
      } finally {
        setLoading(false);
      }
    },
  });

  const handleResend = async () => {
    setLoading(true);
    setErrorMsg("");
    setWarningMsg("");
    let result;
    if (method === "whatsapp") {
      result = await requestResetCode(formik.values.number_phone);
    } else {
      result = await requestResetByEmail(formik.values.email);
    }
    if (result.success) {
      startCooldown();
      Swal.fire({
        title: "Código reenviado",
        text:
          method === "whatsapp"
            ? "Revisa tu WhatsApp."
            : "Revisa tu correo electrónico.",
        icon: "success",
        timer: 1800,
        showConfirmButton: false,
      });
    } else if (result.rateLimit) {
      setWarningMsg(result.message);
    } else {
      setErrorMsg(result.message);
    }
    setLoading(false);
  };

  const handleMethodChange = (newMethod) => {
    setMethod(newMethod);
    setErrorMsg("");
    setWarningMsg("");
    formik.setTouched({});
    formik.setErrors({});
  };

  return (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="center"
      sx={{ minHeight: "70vh", py: 4, px: 2 }}
    >
      <Paper
        elevation={8}
        sx={{
          p: { xs: 3, sm: 4 },
          width: "100%",
          maxWidth: 480,
          borderRadius: 4,
          backgroundImage: `url(${imgbgregister})`,
          backgroundSize: "cover",
          backgroundColor: "rgba(255,255,255,0.95)",
        }}
      >
        {/* Header */}
        <Typography
          variant="h5"
          fontWeight="bold"
          textAlign="center"
          gutterBottom
          sx={{ color: "#801F56" }}
        >
          Recuperar Contraseña
        </Typography>

        {/* Stepper */}
        <Stepper
          activeStep={activeStep}
          alternativeLabel
          sx={{
            mb: 3,
            "& .MuiStepIcon-root.Mui-active": { color: "#801F56" },
            "& .MuiStepIcon-root.Mui-completed": { color: "#801F56" },
          }}
        >
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {/* Error message */}
        {errorMsg && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErrorMsg("")}>
            {errorMsg}
          </Alert>
        )}

        {/* Rate limit warning */}
        {warningMsg && (
          <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setWarningMsg("")}>
            ⏳ {warningMsg}
          </Alert>
        )}

        {/* Form */}
        <form onSubmit={formik.handleSubmit} noValidate>
          {activeStep === 0 && (
            <StepRequestCode
              formik={formik}
              loading={loading}
              method={method}
              onMethodChange={handleMethodChange}
            />
          )}
          {activeStep === 1 && (
            <StepCode
              formik={formik}
              loading={loading}
              countdown={countdown}
              onResend={handleResend}
              method={method}
              phone={formik.values.number_phone}
              email={formik.values.email}
            />
          )}
          {activeStep === 2 && (
            <StepNewPassword formik={formik} loading={loading} />
          )}
        </form>

        {/* Footer link */}
        <Typography textAlign="center" sx={{ mt: 3 }}>
          ¿Ya la recordaste?{" "}
          <Link
            to="/iniciar-sesion"
            style={{ color: "#801F56", fontWeight: 600 }}
          >
            Inicia Ahora
          </Link>
        </Typography>
      </Paper>
    </Box>
  );
};

export default ResetPassword;
